import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Share2, MessageCircle, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/common/Button';
import { Loader } from '../components/common/Loader';
import { CommentSection } from '../components/artwork/CommentSection';
import { getArtworkById } from '../services/artworkService';
import { addToCart } from '../services/cartService';
import { trackArtworkView } from '../services/analyticsService';
import { useAuth } from '../context/AuthContext';
import { useWishlist } from '../hooks/useWishlist';
import { useLike } from '../hooks/useLike';
import { Artwork } from '../types';
import { motion, AnimatePresence } from 'framer-motion';

// Image Carousel Component
interface ImageCarouselProps {
  images: string[];
  title: string;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
    }),
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };

  const paginate = (newDirection: number) => {
    setDirection(newDirection);
    setCurrentIndex((prevIndex) => {
      let nextIndex = prevIndex + newDirection;
      if (nextIndex < 0) nextIndex = images.length - 1;
      if (nextIndex >= images.length) nextIndex = 0;
      return nextIndex;
    });
  };

  const goToSlide = (index: number) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  // If only one image, render without carousel controls
  if (images.length === 1) {
    return (
      <motion.img
        initial={{ scale: 1.1, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        src={images[0]}
        alt={title}
        className="w-full h-96 object-cover"
      />
    );
  }

  return (
    <div className="relative h-96 overflow-hidden bg-gray-900">
      <AnimatePresence initial={false} custom={direction}>
        <motion.img
          key={currentIndex}
          src={images[currentIndex]}
          alt={`${title} - Image ${currentIndex + 1}`}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: 'spring', stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
          }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={1}
          onDragEnd={(e, { offset, velocity }) => {
            const swipe = swipePower(offset.x, velocity.x);
            if (swipe < -swipeConfidenceThreshold) {
              paginate(1);
            } else if (swipe > swipeConfidenceThreshold) {
              paginate(-1);
            }
          }}
          className="absolute w-full h-full object-cover cursor-grab active:cursor-grabbing"
        />
      </AnimatePresence>

      {/* Navigation Arrows */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => paginate(-1)}
        className="absolute left-3 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
      >
        <ChevronLeft size={24} className="text-gray-800" />
      </motion.button>

      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => paginate(1)}
        className="absolute right-3 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
      >
        <ChevronRight size={24} className="text-gray-800" />
      </motion.button>

      {/* Dot Indicators */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
        {images.map((_, index) => (
          <motion.button
            key={index}
            onClick={() => goToSlide(index)}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
              index === currentIndex
                ? 'bg-white w-8'
                : 'bg-white/50 hover:bg-white/75'
            }`}
          />
        ))}
      </div>

      {/* Image Counter */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 px-3 py-1 bg-black/50 backdrop-blur-sm rounded-full">
        <span className="text-white text-sm font-medium">
          {currentIndex + 1} / {images.length}
        </span>
      </div>
    </div>
  );
};

// Thumbnail Strip Component
interface ThumbnailStripProps {
  images: string[];
  currentIndex: number;
  onSelect: (index: number) => void;
}

const ThumbnailStrip: React.FC<ThumbnailStripProps> = ({ images, currentIndex, onSelect }) => {
  if (images.length <= 1) return null;

  return (
    <div className="px-4 py-3 bg-gray-50">
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {images.map((image, index) => (
          <motion.button
            key={index}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onSelect(index)}
            className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all duration-200 ${
              index === currentIndex
                ? 'ring-2 ring-primary ring-offset-2'
                : 'opacity-60 hover:opacity-100'
            }`}
          >
            <img
              src={image}
              alt={`Thumbnail ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </motion.button>
        ))}
      </div>
    </div>
  );
};

const ArtworkDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [artwork, setArtwork] = useState<Artwork | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [showComments, setShowComments] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const { isWishlisted, toggleWishlist, loading: wishlistLoading } = useWishlist(id || '');
  const { isLikedByUser, toggleLike, loading: likeLoading } = useLike(id || '', artwork?.artistId || '');

  useEffect(() => {
    loadArtwork();
  }, [id]);

  const loadArtwork = async () => {
    if (!id || !user) return;
    
    setLoading(true);
    try {
      const data = await getArtworkById(id);
      setArtwork(data);
      if (data?.sizes && data.sizes.length > 0) {
        setSelectedSize(data.sizes[0]);
      }
      // Track view
      await trackArtworkView(id, user.id);
    } catch (error) {
      console.error('Error loading artwork:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!artwork || !user) return;
    
    try {
      await addToCart(user.id, {
        artworkId: artwork.id,
        artwork,
        quantity: 1,
      });
      alert('Added to cart!');
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Failed to add to cart');
    }
  };

  const handleShare = async () => {
    if (navigator.share && artwork) {
      try {
        await navigator.share({
          title: artwork.title,
          text: `Check out ${artwork.title} by ${artwork.artistName}`,
          url: window.location.href,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  // Get all images (use imageUrls if available, fallback to single imageUrl)
  const getAllImages = (): string[] => {
    if (!artwork) return [];
    if (artwork.imageUrls && artwork.imageUrls.length > 0) {
      return artwork.imageUrls;
    }
    return [artwork.imageUrl];
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="h-screen flex items-center justify-center">
        <p className="text-gray-500">Artwork not found</p>
      </div>
    );
  }

  const images = getAllImages();

  return (
    <div className="pb-8 bg-gray-50 min-h-screen">
      {/* Header with Image Carousel */}
      <div className="relative">
        <ImageCarousel images={images} title={artwork.title} />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none"></div>
        
        {/* Back Button */}
        <motion.button
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 p-3 bg-white rounded-full shadow-xl z-20"
        >
          <ArrowLeft size={24} />
        </motion.button>
        
        {/* Action Buttons */}
        <div className="absolute top-4 right-4 flex gap-2 z-20">
          <motion.button
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            whileTap={{ scale: 0.9 }}
            onClick={toggleWishlist}
            disabled={wishlistLoading}
            className="p-3 bg-white rounded-full shadow-xl"
          >
            <Heart 
              size={24} 
              className={isWishlisted ? 'text-red-500 fill-red-500' : 'text-gray-700'}
            />
          </motion.button>
          <motion.button
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleShare}
            className="p-3 bg-white rounded-full shadow-xl"
          >
            <Share2 size={24} />
          </motion.button>
        </div>

        {/* Stats Overlay */}
        <div className="absolute bottom-4 right-4 flex gap-3 z-20">
          <div className="flex items-center gap-1 px-3 py-2 bg-white/90 backdrop-blur-sm rounded-full">
            <Eye size={16} className="text-gray-600" />
            <span className="text-sm font-medium">{artwork.views}</span>
          </div>
          <div className="flex items-center gap-1 px-3 py-2 bg-white/90 backdrop-blur-sm rounded-full">
            <Heart size={16} className="text-red-500" />
            <span className="text-sm font-medium">{artwork.likes}</span>
          </div>
        </div>
      </div>

      {/* Thumbnail Strip (only shows if multiple images) */}
      <ThumbnailStrip
        images={images}
        currentIndex={currentImageIndex}
        onSelect={setCurrentImageIndex}
      />

      {/* Content */}
      <div className="p-4">
        {/* Artist Info */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          onClick={() => navigate(`/profile/${artwork.artistId}`)}
          className="flex items-center gap-3 mb-6 bg-white rounded-xl p-4 shadow-sm cursor-pointer hover:shadow-md transition-all"
        >
          <div className="w-14 h-14 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white font-bold text-xl overflow-hidden">
            {artwork.artistAvatar ? (
              <img src={artwork.artistAvatar} alt={artwork.artistName} className="w-full h-full object-cover" />
            ) : (
              artwork.artistName.charAt(0)
            )}
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-800">{artwork.artistName}</h3>
            <p className="text-sm text-gray-500">Artist</p>
          </div>
          <button className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-indigo-900 transition-colors">
            View Profile
          </button>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-6"
        >
          <h1 className="text-3xl font-bold text-gray-800 mb-2">{artwork.title}</h1>
          <div className="flex items-center gap-2">
            <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
              {artwork.category}
            </span>
            {images.length > 1 && (
              <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                {images.length} Images
              </span>
            )}
          </div>
        </motion.div>

        {/* Description */}
        {artwork.description && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mb-6 bg-white rounded-xl p-4 shadow-sm"
          >
            <h4 className="font-semibold text-gray-800 mb-2">Description</h4>
            <p className="text-gray-600 leading-relaxed">{artwork.description}</p>
          </motion.div>
        )}

        {/* Sizes */}
        {artwork.sizes && artwork.sizes.length > 0 && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mb-6"
          >
            <h4 className="font-semibold text-gray-800 mb-3">Available Sizes</h4>
            <div className="flex gap-2 flex-wrap">
              {artwork.sizes.map((size) => (
                <motion.button
                  key={size}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedSize(size)}
                  className={`px-4 py-2 rounded-lg border-2 transition-all ${
                    selectedSize === size
                      ? 'border-primary bg-primary text-white shadow-lg'
                      : 'border-gray-300 text-gray-700 hover:border-primary'
                  }`}
                >
                  {size}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Price & Actions */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 shadow-md"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Price</p>
              <p className="text-3xl font-bold text-primary">₹{artwork.price.toLocaleString()}</p>
            </div>
          </div>

          <div className="flex gap-3">
            <Button onClick={handleAddToCart} fullWidth className="shadow-lg">
              🛒 Add to Cart
            </Button>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={toggleLike}
              disabled={likeLoading}
              className={`px-6 py-3 rounded-lg border-2 transition-all ${
                isLikedByUser
                  ? 'border-red-500 bg-red-50 text-red-500'
                  : 'border-gray-300 text-gray-700 hover:border-red-500'
              }`}
            >
              <Heart size={20} className={isLikedByUser ? 'fill-red-500' : ''} />
            </motion.button>
          </div>
        </motion.div>

        {/* Comments Toggle */}
        <motion.button
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.7 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowComments(!showComments)}
          className="w-full bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all flex items-center justify-between mb-4"
        >
          <div className="flex items-center gap-2">
            <MessageCircle size={24} className="text-primary" />
            <span className="font-semibold text-gray-800">Comments</span>
          </div>
          <motion.div
            animate={{ rotate: showComments ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            ▼
          </motion.div>
        </motion.button>

        {/* Comments Section */}
        <AnimatePresence>
          {showComments && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <CommentSection artworkId={artwork.id} artistId={artwork.artistId} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ArtworkDetail;